import { Box, Button, CircularProgress, TextField } from "@mui/material";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import { QuestionAnswerTypeEnum } from "@/services";
import SectionAccordion from "./SectionAccordion";
import MultiSelectGroup from "./MultiSelectGroup";
import ChooseOneGroup from "./ChooseOneGroup";
import SocialSubFieldCards from "./SocialSubFieldCards";
import { useSocialHistoryFields } from "../hooks/use-social-history-fields";
import { useSocialHistoryValidation } from "../hooks/use-social-history-validation";
import { useRegisterSectionSave } from "../pending-section-saves-context";
import {
  FREE_TEXT_MAX_ROWS,
  FREE_TEXT_PLACEHOLDER,
} from "../readmission-f2f-constant";
import { SocialHistoryQuestion } from "../readmission-f2f-types";
import { emptyGroupValue, isGroupValue } from "../readmission-f2f-utils";

export interface SocialHistorySectionProps {
  title: string;
  expanded: boolean;
  onToggle: () => void;
}

const SocialHistorySection = ({
  title,
  expanded,
  onToggle,
}: SocialHistorySectionProps) => {
  const {
    store,
    isLoading,
    isSaving,
    isDirty,
    setValue,
    save,
    ordered,
    summaryLines,
    orderedOptions,
    exclusiveOf,
    chooseOneOf,
    subFieldGroups,
    subText,
    subNone,
    setSubText,
    setSubNone,
    clearDeselected,
  } = useSocialHistoryFields();
  const { error, validate, clearError } = useSocialHistoryValidation(
    ordered,
    store
  );
  const answer = (uuid: string, value: Parameters<typeof setValue>[1]) => {
    clearError();
    setValue(uuid, value);
  };
  const saveIfValid = () => {
    if (validate()) save();
  };
  useRegisterSectionSave(title, isDirty, save);

  const subFields = (q: SocialHistoryQuestion, selected: string[]) => (
    <SocialSubFieldCards
      groups={subFieldGroups(q, selected)}
      subText={subText}
      subNone={subNone}
      onSubTextChange={setSubText}
      onSubNoneToggle={setSubNone}
    />
  );

  return (
    <SectionAccordion
      title={title}
      expanded={expanded}
      onToggle={onToggle}
      summaryLines={summaryLines}
    >
      {isLoading ? (
        <Flex justifyContent="center" p={2}>
          <CircularProgress size={22} />
        </Flex>
      ) : (
        <>
          {ordered.map((q) => {
            const current = store[q.uuid];

            if (q.answerType === QuestionAnswerTypeEnum.Text) {
              const value = chooseOneOf(q);
              return (
                <Box
                  key={q.uuid}
                  sx={{ border: "1px solid #E0E0E0", borderRadius: 1, p: 1.5 }}
                  display="flex"
                  flexDirection="column"
                  gap={1}
                >
                  <Text fontWeight="bold">{q.title}</Text>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    maxRows={FREE_TEXT_MAX_ROWS}
                    placeholder={FREE_TEXT_PLACEHOLDER}
                    value={value.freeText}
                    onChange={(e) =>
                      setValue(q.uuid, { ...value, freeText: e.target.value })
                    }
                  />
                </Box>
              );
            }

            if (
              q.answerType === QuestionAnswerTypeEnum.Boolean ||
              q.chooseOne
            ) {
              const value = chooseOneOf(q);
              return (
                <ChooseOneGroup
                  key={q.uuid}
                  title={q.title}
                  options={orderedOptions(q)}
                  value={value}
                  onChange={(next) => {
                    clearDeselected(q, [value.selected], [next.selected]);
                    answer(q.uuid, next);
                  }}
                  withFreeText={q.withFreeText}
                >
                  {subFields(q, [value.selected])}
                </ChooseOneGroup>
              );
            }

            const value =
              current && isGroupValue(current) ? current : emptyGroupValue();
            return (
              <MultiSelectGroup
                key={q.uuid}
                title={q.title}
                options={orderedOptions(q)}
                value={value}
                exclusive={exclusiveOf(q)}
                onChange={(next) => {
                  clearDeselected(q, value.selected, next.selected);
                  answer(q.uuid, next);
                }}
                withDetails={false}
              >
                {subFields(q, value.selected)}
              </MultiSelectGroup>
            );
          })}
          <Flex justifyContent="flex-end" alignItems="center" gap={2} mt={1}>
            {error && (
              <Text size={12} c="error.main">
                {error}
              </Text>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={saveIfValid}
              disabled={isSaving || !isDirty}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </Flex>
        </>
      )}
    </SectionAccordion>
  );
};

export default SocialHistorySection;
